import {
  Background,
  Controls,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  type NodeMouseHandler,
} from "@xyflow/react";
import { Spin } from "antd";
import { memo, useCallback, useMemo } from "react";
import { ScenarioNode, ScenarioNodeType } from "types/scenario";
import { countBranches } from "../utils";
import { toReactFlow, type FlowCanvasNode } from "../toReactFlow";
import { FlowAddMenu } from "./FlowAddMenu";
import { FlowCanvasProvider } from "./FlowCanvasContext";
import { AddBranchFlowNode, CaptionFlowNode, ScenarioFlowNode } from "./FlowNodeCard";
import "@xyflow/react/dist/style.css";
import "./FlowEditor.scss";

const flowNodeTypes = {
  scenario: ScenarioFlowNode,
  missing: ScenarioFlowNode,
  addBranch: AddBranchFlowNode,
  caption: CaptionFlowNode,
};

type FlowCanvasProps = {
  nodes: ScenarioNode[];
  startNodeKey: string;
  selectedNodeId: number | null;
  onSelectNode: (id: number) => void;
  onAddNode: (type: ScenarioNodeType) => void;
  onAddBranchNode: (choiceKey: string, optionIndex: number, type: ScenarioNodeType) => void;
};

const FlowCanvasInner = memo(
  ({ nodes, startNodeKey, selectedNodeId, onSelectNode, onAddNode, onAddBranchNode }: FlowCanvasProps) => {
    const layout = useMemo(() => toReactFlow(nodes, startNodeKey), [nodes, startNodeKey]);

    const rfNodes = useMemo(
      () =>
        layout.nodes.map((node) => {
          if (node.type !== "scenario") {
            return { ...node, selected: false };
          }

          return { ...node, selected: node.data.node.id === selectedNodeId };
        }),
      [layout.nodes, selectedNodeId],
    );

    const handleNodeClick = useCallback<NodeMouseHandler<FlowCanvasNode>>(
      (_event, node) => {
        if (node.type !== "scenario") {
          return;
        }

        onSelectNode(node.data.node.id);
      },
      [onSelectNode],
    );

    const actions = useMemo(() => ({ onAddBranchNode }), [onAddBranchNode]);

    return (
      <FlowCanvasProvider value={actions}>
        <ReactFlow
          nodes={rfNodes}
          edges={layout.edges}
          nodeTypes={flowNodeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          edgesReconnectable={false}
          elementsSelectable
          panOnDrag
          zoomOnScroll
          zoomOnPinch
          selectionOnDrag={false}
          deleteKeyCode={null}
          multiSelectionKeyCode={null}
          onNodeClick={handleNodeClick}
          fitView
          minZoom={0.35}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={16} color="#dde5ef" />
          <Controls showInteractive={false} />
          <Panel position="bottom-center" className="flowCanvasPanel">
            <FlowAddMenu className="flowAddBtn" label="Thêm node vào luồng" onPick={onAddNode} />
          </Panel>
        </ReactFlow>
      </FlowCanvasProvider>
    );
  },
);

FlowCanvasInner.displayName = "FlowCanvasInner";

type FlowEditorProps = {
  name: string;
  startNodeKey: string;
  nodes: ScenarioNode[];
  loading?: boolean;
  selectedNodeId: number | null;
  onSelectNode: (id: number) => void;
  onAddNode: (type: ScenarioNodeType) => void;
  onAddBranchNode: (choiceKey: string, optionIndex: number, type: ScenarioNodeType) => void;
};

export const FlowEditor = memo(
  ({
    name,
    startNodeKey,
    nodes,
    loading,
    selectedNodeId,
    onSelectNode,
    onAddNode,
    onAddBranchNode,
  }: FlowEditorProps) => {
    const branchCount = countBranches(nodes);

    return (
      <section className="flowEditor">
        <div className="flowEditorHead">
          <h2>{name}</h2>
          <div className="flowEditorMeta">
            <span>{nodes.length} nodes</span>
            <span>{branchCount} nhánh</span>
          </div>
        </div>

        <div className="flowCanvas">
          {loading ? (
            <div className="flowCanvasLoading">
              <Spin size="small" />
            </div>
          ) : (
            <ReactFlowProvider>
              <FlowCanvasInner
                nodes={nodes}
                startNodeKey={startNodeKey}
                selectedNodeId={selectedNodeId}
                onSelectNode={onSelectNode}
                onAddNode={onAddNode}
                onAddBranchNode={onAddBranchNode}
              />
            </ReactFlowProvider>
          )}
        </div>
      </section>
    );
  },
);

FlowEditor.displayName = "FlowEditor";
